import { useMemo } from 'react';

import type { ShareChannel } from './shared/types';

/**
 * Utility hook to filter out specific share channels.
 *
 * If a channel is present in the `filters` object with a value of `false`, it will be excluded from the returned array.
 *
 * If a channel is not present in the `filters` object, it will be excluded by default, unless the `default` option is set to `true`, in which case it will be included.
 *
 * @example
 * const channels = useMemo(() => ['email', 'facebook', 'twitter'] as ShareChannel[], []);
 * const filters = useMemo(() => ({ email: false, twitter: true }) as const, []);
 * const filteredActions = useFilteredShareChannels(channels, filters); // Returns ['twitter']
 * const filteredActions = useFilteredShareChannels(channels, filters, { default: true }); // Returns ['facebook', 'twitter']
 */
export function useFilteredShareChannels<SH extends ShareChannel>(
	channels: readonly SH[],
	filters: Partial<Record<SH, boolean>>,
	options: {
		/**
		 * If set to `true`, channels not present in the `filters` object will be included in the result.
		 */
		default?: boolean;
	} = {},
): readonly SH[] {
	const def = options.default ?? false;
	return useMemo(
		() =>
			channels.filter((channel) => {
				if (!(channel in filters)) {
					return def;
				}
				return filters[channel];
			}),
		[channels, filters, def],
	);
}
